import React, { Suspense, useEffect, useState } from 'react';
import { X, MapPin, Building2, CheckCircle2, Camera, Clock, Compass } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { resolveAreaIntelligence } from '../../lib/areaIntelligence/config';
import { buildPlatformAreaContext } from '../../lib/areaIntelligence/platformProvider';
import { enrichAreaContext } from '../../lib/areaIntelligence/googleProvider';

const AreaMapCanvas = React.lazy(() => import('./AreaMapCanvas'));

export const AREA_INTELLIGENCE_PANEL_TEST_ID = 'area-intelligence-panel';

/**
 * JC Area Intelligence panel — an expandable side panel the concierge opens
 * so a user can visually understand a place before arriving.
 *
 * Renders real platform data always (photos, bases, highlights); Google
 * enrichment (Street View, places, route times) appears when configured.
 * Sections with no real data are omitted — no placeholders, no fake pins.
 */
export default function AreaIntelligencePanel({ location, open, onClose }) {
  const { t, locale } = useLanguage();
  const [context, setContext] = useState(null);
  const [streetViewFailed, setStreetViewFailed] = useState(false);
  const intel = resolveAreaIntelligence();

  useEffect(() => {
    let cancelled = false;
    if (!open || !location) {
      setContext(null);
      return undefined;
    }
    const base = buildPlatformAreaContext(location, locale);
    setStreetViewFailed(false);
    setContext(base);
    if (intel.mode === 'google' && base) {
      enrichAreaContext(base).then((enriched) => {
        if (!cancelled) setContext(enriched);
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, location?.id, locale]);

  if (!open || !intel.enabled || !context) return null;

  const { capabilities } = intel;
  const streetViewSrc = streetViewFailed ? null : context.streetView;
  const previewSrc = streetViewSrc || context.photos[0] || null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] flex flex-col bg-ivory border-l border-champagne-200 shadow-[-8px_0_40px_rgba(15,23,42,0.12)]"
      data-testid={AREA_INTELLIGENCE_PANEL_TEST_ID}
      role="dialog"
      aria-label={t('area_intel.title', 'Area Intelligence')}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-champagne-200">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-signal-blue to-military-gold flex items-center justify-center flex-shrink-0">
            <Compass className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-military-gold font-semibold">
              {t('area_intel.title', 'Area Intelligence')}
            </p>
            <h2 className="font-heading text-lg font-bold text-slate-900 truncate">
              {context.location.name}
              {context.location.region ? (
                <span className="text-slate-500 font-normal text-sm ml-2">{context.location.region}</span>
              ) : null}
            </h2>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full text-slate-500 hover:text-slate-800 hover:bg-champagne-100 transition-colors"
          aria-label={t('area_intel.close', 'Close area intelligence')}
          data-testid="area-intel-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
        {/* Visual preview: Street View when live, curated photo otherwise */}
        {previewSrc && (
          <div>
            <div className="rounded-xl overflow-hidden border border-champagne-200 shadow-sm">
              <img
                src={previewSrc}
                alt={context.location.name}
                className="w-full h-48 object-cover"
                loading="lazy"
                onError={() => setStreetViewFailed(true)}
              />
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-1">
              <Camera className="w-3 h-3" />
              {streetViewSrc
                ? t('area_intel.street_view_label', 'Street View')
                : t('area_intel.photo_label', 'Neighborhood photo')}
            </p>
          </div>
        )}

        {/* Tagline */}
        {context.tagline && (
          <p className="text-slate-700 text-sm leading-relaxed">{context.tagline}</p>
        )}

        {/* Map canvas — design-gated; absent entirely unless enabled */}
        {capabilities.mapCanvas && (
          <Suspense fallback={null}>
            <AreaMapCanvas location={context.location} pins={context.places} />
          </Suspense>
        )}

        {/* Nearby bases (real platform data) */}
        {context.bases.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              {t('locations.nearby_military_bases', 'Nearby Military Bases')}
            </p>
            <div className="flex flex-wrap gap-2">
              {context.bases.map((base) => (
                <span
                  key={base}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-champagne-200 text-slate-700 text-sm rounded-full"
                >
                  <Building2 className="w-3.5 h-3.5 text-military-gold" />
                  {base}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Route-time cards (google enrichment; hidden until live data exists) */}
        {context.routes.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              {t('area_intel.routes_title', 'Commute Times')}
            </p>
            <div className="space-y-2">
              {context.routes.map((route) => (
                <div
                  key={route.destination}
                  className="flex items-center justify-between bg-white border border-champagne-200 rounded-xl px-4 py-3"
                >
                  <span className="text-sm text-slate-700">{route.destination}</span>
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {route.driveText}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nearby places cards (google enrichment; hidden until live data exists) */}
        {context.places.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              {t('area_intel.places_title', 'Schools, Shops & Essentials')}
            </p>
            <div className="space-y-2">
              {context.places.map((place) => (
                <div key={place.id} className="bg-white border border-champagne-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-medium text-slate-800">{place.name}</p>
                  <p className="text-xs text-slate-500">
                    {place.category}
                    {place.distanceText ? ` · ${place.distanceText}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Why families like it here (real curated content) */}
        {context.highlights.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              {t('locations.why_families', 'Why families like it here')}
            </p>
            <ul className="space-y-2">
              {context.highlights.map((highlight) => (
                <li key={highlight} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Photo carousel (real curated assets) */}
        {context.photos.length > 1 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              {t('area_intel.photos_title', 'Around the Neighborhood')}
            </p>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {context.photos.slice(0, 8).map((src) => (
                <img
                  key={src}
                  src={src}
                  alt={context.location.name}
                  className="h-28 w-44 flex-shrink-0 object-cover rounded-xl border border-champagne-200"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Honest mode footer */}
      {context.mode === 'platform' && (
        <div className="px-5 py-3 bg-champagne-100/60 border-t border-champagne-200">
          <p className="text-[11px] text-slate-600 flex items-center gap-1.5">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            {t(
              'area_intel.platform_mode_note',
              'Street View, live places, and commute times activate when location services are connected.',
            )}
          </p>
        </div>
      )}
    </div>
  );
}
